import { zodResolver } from '@hookform/resolvers/zod';
import { CrmTaskStatus } from '@plato/data/prisma';
import { ArrowUpRight, CalendarIcon, CircleUserRoundIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { z } from 'zod';
import { ModalForm } from '../../../components/form-components/modal-form.tsx';
import { Label } from '../../../components/ui/label.tsx';
import { Notice } from '../../../components/ui/notice.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useDismissibleHint } from '../../../lib/hooks/use-dismissible-hint.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { crmTaskInputDtoSchema } from '../../../server/dto/crm-task-input-dto.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { UsersSelect } from '../../shared/components/form-items-with-fetch/users-select.tsx';
import { DueDatePicker, TaskLinksInput } from './task-form-fields.tsx';

const createTaskBulkFormValuesSchema = (assigneeRequiredMessage: string) =>
    crmTaskInputDtoSchema.omit({ assignedToId: true }).extend({
        assignedToIds: z.array(z.string().uuid()).min(1, { message: assigneeRequiredMessage }),
    });

export type TaskBulkFormValues = z.infer<ReturnType<typeof createTaskBulkFormValuesSchema>>;

const bulkCreationHintStorageKey = 'plato-dismissed-hint-bulk-task-creation';

type TaskBulkFormDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: TaskBulkFormValues) => void;
    isSubmitting: boolean;
    title: string;
    initialValues?: Partial<TaskBulkFormValues>;
};

export const TaskBulkFormDialog = ({
    open,
    onClose,
    onSubmit,
    isSubmitting,
    title,
    initialValues,
}: TaskBulkFormDialogProps): ReactNode => {
    const { t } = useLocale('tasks');
    const { enableFileUpload } = useAppConfig();
    const { currentSessionId } = useVoiceRecording();
    const { isDismissed: isHintDismissed, dismiss: dismissHint } =
        useDismissibleHint(bulkCreationHintStorageKey);

    const defaultValues = useMemo(() => {
        return {
            dueDate: new Date(),
            links: [],
            assignedToIds: [],
            status: CrmTaskStatus.OPEN,
            description: undefined,
            attachments: [],
            ...initialValues,
        };
    }, [initialValues]);

    const schema = useMemo(
        () => createTaskBulkFormValuesSchema(t('form__assignee_required', { ns: 'common' })),
        [t],
    );

    const form = useForm<TaskBulkFormValues>({
        defaultValues,
        resolver: zodResolver(schema),
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty, errors: formErrors },
    } = form;

    const assigneeCount = useWatch({ control, name: 'assignedToIds' }).length;

    const submitText = t('tasks__form_create_submit', { count: assigneeCount });

    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(() => {
            onClose();
        });
    };

    const submit = handleSubmit((values) => {
        onSubmit(values);
    });

    return (
        <Dialog showClose open={open} onClose={handleClose}>
            <DialogForm>
                <DialogForm.Header title={title} />
                <DialogForm.Content>
                    <DialogForm.ItemList>
                        <DialogForm.Item>
                            <Label htmlFor="description" className="sr-only">
                                {t('description')}
                            </Label>
                            <DialogForm.Body>
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field }) => (
                                        <RichTextInput
                                            trackingContext={{ source: 'task' }}
                                            placeholder={t('tasks__description_placeholder')}
                                            {...field}
                                        />
                                    )}
                                />
                            </DialogForm.Body>
                            <DialogForm.Error error={formErrors.description} />
                        </DialogForm.Item>
                    </DialogForm.ItemList>
                    <DialogForm.Group>
                        <DialogForm.ItemList subgrid>
                            <DialogForm.Item>
                                <DialogForm.Label icon={ArrowUpRight}>
                                    {t('related_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex-col">
                                    <Controller
                                        control={control}
                                        name="links"
                                        render={({ field }) => <TaskLinksInput {...field} />}
                                    />
                                </DialogForm.Body>
                                <ModalForm.Error errorMessage={formErrors.links} />
                            </DialogForm.Item>

                            <DialogForm.Item>
                                <DialogForm.Label htmlFor="dueDate" icon={CalendarIcon}>
                                    {t('due_date', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="dueDate"
                                        render={({ field }) => <DueDatePicker {...field} />}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={formErrors.dueDate} />
                            </DialogForm.Item>
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label htmlFor="assignedToIds" icon={CircleUserRoundIcon} required>
                                    {t('assigned_to', {
                                        ns: 'common',
                                    })}
                                </DialogForm.Label>
                                <DialogForm.Body className="min-w-0 flex-col gap-1">
                                    <Controller
                                        control={control}
                                        name="assignedToIds"
                                        render={({ field }) => (
                                            <UsersSelect
                                                multiple
                                                {...field}
                                                error={!!formErrors.assignedToIds}
                                                className="w-full justify-start"
                                            />
                                        )}
                                    />
                                    <DialogForm.Error error={formErrors.assignedToIds} />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </DialogForm.ItemList>
                        {!isHintDismissed && (
                            <Notice
                                variant="info"
                                icon={null}
                                className="col-span-2"
                                role="status"
                                description={
                                    <Trans
                                        i18nKey="tasks__bulk_creation_hint"
                                        ns="tasks"
                                        components={{ strong: <strong className="font-semibold" /> }}
                                    />
                                }
                                onDismiss={dismissHint}
                                dismissLabel={t('tasks__bulk_creation_hint_dismiss')}
                            />
                        )}
                        {enableFileUpload && (
                            <DialogForm.ItemList subgrid>
                                <Controller
                                    control={control}
                                    name="attachments"
                                    render={({ field }) => (
                                        <FileUploader
                                            {...field}
                                            value={field.value ?? []}
                                            analyticsContext="task"
                                        />
                                    )}
                                />
                            </DialogForm.ItemList>
                        )}
                    </DialogForm.Group>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    isLoading={isSubmitting}
                    onCancel={handleClose}
                    onSubmit={submit}
                    submitText={submitText}
                    disabled={!!currentSessionId}
                />
            </DialogForm>
        </Dialog>
    );
};
